import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { db } from '../lib/cloudsql';
import { formatNumberInput, formatUnitPriceDisplay, parseNumberInput } from '../utils/formatNumberInput';

// 基本介面定義
interface ServiceRate {
  id?: string;
  customer_id?: string;
  service_type: string;
  hourly_rate?: number;  // 資料庫欄位名稱
  unit_price?: number;   // 兼容欄位名稱
  unit?: string;
  year?: number;
  description?: string;
  is_active?: boolean;
  charge_type?: 'fixed' | 'percentage'; // 收費類型：固定金額或百分比
  created_at?: string;
}

const ServiceRates: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [serviceRates, setServiceRates] = useState<ServiceRate[]>([]);
  const [filteredRates, setFilteredRates] = useState<ServiceRate[]>([]);
  // const [customers] = useState<Array<{ id: string; name: string }>>([]);
  const [currentRate, setCurrentRate] = useState<ServiceRate | null>(null);
  const [showCreateRate, setShowCreateRate] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchServiceRates();
    fetchCustomers();
    setLoading(false);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // 根據選中的年度篩選資料
    if (selectedYear === 0) {
      setFilteredRates(serviceRates);
    } else {
      setFilteredRates(serviceRates.filter(rate => rate.year && rate.year === selectedYear));
    }
  }, [serviceRates, selectedYear]);

  const fetchServiceRates = async () => {
    try {
      const data = await db.query<ServiceRate>('service_rates', {
        orderBy: [{ field: 'created_at', direction: 'desc' }]
      });
      // 將 hourly_rate 映射到 unit_price，並從 description 解析 unit 和 year
      const mappedData = (data || []).map(rate => {
        // 從 description 解析 unit 和 year（如果有的話）
        let unit = '小時'; // 預設值
        let year = new Date().getFullYear(); // 預設值
        if (rate.description) {
          const unitMatch = rate.description.match(/單位:\s*([^|]+)/);
          if (unitMatch) {
            unit = unitMatch[1].trim();
          }
          const yearMatch = rate.description.match(/年份:\s*(\d+)/);
          if (yearMatch) {
            year = parseInt(yearMatch[1]);
          }
        }
        return {
          ...rate,
          unit_price: rate.hourly_rate || rate.unit_price || 0,
          unit,
          year,
          charge_type: rate.charge_type || 'fixed' // 預設為固定金額
        };
      });
      setServiceRates(mappedData);
    } catch {
      // 靜默處理錯誤
    }
  };

  const fetchCustomers = async () => {
    try {
      // 不需要客戶資料，但保留函數以保持一致性
    } catch {
      // Error fetching customers
    }
  };

  // 取得所有年度選項
  const getYearOptions = () => {
    const years = Array.from(new Set(
      serviceRates
        .map(rate => rate.year)
        .filter((year): year is number => year !== undefined && year !== null && typeof year === 'number')
    )).sort((a, b) => b - a);
    return years;
  };

  const createNewRate = () => {
    setCurrentRate({
      service_type: '',
      unit_price: 0,
      unit: '小時',
      year: selectedYear === 0 ? new Date().getFullYear() : selectedYear,
      charge_type: 'fixed' // 預設為固定金額
    });
    setIsEditing(false);
    setShowCreateRate(true);
  };

  const editRate = (rate: ServiceRate) => {
    setCurrentRate({ ...rate });
    setIsEditing(true);
    setShowCreateRate(true);
  };

  const copyRate = (rate: ServiceRate) => {
    // 完全清理物件，只保留必要的欄位
    setCurrentRate({
      service_type: `${rate.service_type} (複製)`,
      unit_price: rate.unit_price,
      unit: rate.unit,
      year: rate.year,
      charge_type: rate.charge_type || 'fixed'
    });
    setIsEditing(false); // 確保是新增模式
    setShowCreateRate(true);
  };

  const saveRate = async () => {
    if (!currentRate) return;
    
    try {
      // 準備保存資料：將 unit_price 映射到 hourly_rate，將 unit 和 year 放入 description
      const price = currentRate.unit_price || currentRate.hourly_rate || 0;
      const unit = currentRate.charge_type === 'percentage' ? '%' : (currentRate.unit || '小時');
      const year = currentRate.year || new Date().getFullYear();
      const description = `單位: ${unit} | 年份: ${year}`;
      const chargeType = currentRate.charge_type || 'fixed';
      
      if (isEditing) {
        // 更新現有收費項目
        await db.update('service_rates', currentRate.id!, {
          service_type: currentRate.service_type,
          hourly_rate: price,
          description,
          charge_type: chargeType,
          is_active: currentRate.is_active !== false // 預設啟用
        });
      } else {
        // 新增收費項目（複製或新增）
        const newRate = {
          service_type: currentRate.service_type,
          hourly_rate: price,
          description,
          charge_type: chargeType,
          is_active: true // 新增時預設啟用
        };
        
        await db.create('service_rates', newRate);
      }
      
      setShowCreateRate(false);
      setCurrentRate(null);
      setIsEditing(false);
      fetchServiceRates();
    } catch {
      // Error saving service rate
      alert('儲存失敗，請檢查資料是否正確');
    }
  };

  const handleDeleteRate = (_rate: ServiceRate) => {
    // 刪除功能待實作
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">服務收費表</h1>
          <p className="text-gray-600">管理服務收費項目</p>
        </div>
        <button
          onClick={createNewRate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          新增收費項目
        </button>
      </div>

      {/* 年度篩選 */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">篩選年度：</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value) || 0)}
            className="border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
          >
            <option key="all" value={0}>全部年度</option>
            {getYearOptions().map(year => (
              <option key={`year-${year}`} value={year}>{year} 年</option>
            ))}
          </select>
          <span className="text-sm text-gray-500">
            共 {filteredRates.length} 筆資料
          </span>
        </div>
      </div>

      {/* 收費項目表格 */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">收費項目列表</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    收費項目
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    單價
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    單位
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    年度
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRates.map((rate) => (
                  <tr key={rate.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {rate.service_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatUnitPriceDisplay(
                        rate.unit_price ?? rate.hourly_rate ?? 0,
                        rate.charge_type === 'percentage' ? '%' : rate.unit
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        const chargeType = rate.charge_type || 'fixed';
                        if (chargeType === 'percentage') {
                          return '%';
                        }
                        return rate.unit || '小時';
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {rate.year || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        className="text-primary hover:text-primary-dark mr-3"
                        onClick={e => { e.stopPropagation(); copyRate(rate); }}
                        title="複製"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      <button
                        className="text-primary hover:text-primary-dark mr-3"
                        onClick={e => { e.stopPropagation(); editRate(rate); }}
                        title="編輯"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900"
                        onClick={e => { e.stopPropagation(); handleDeleteRate(rate); }}
                        title="刪除"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 新增收費項目模態框 */}
      {showCreateRate && currentRate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* backdrop 灰色遮罩 */}
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40" />
          {/* 內容層 */}
          <div className="relative z-50 w-96 bg-white rounded-md shadow-lg border p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? '編輯收費項目' : currentRate.service_type.includes('(複製)') ? '複製收費項目' : '新增收費項目'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateRate(false);
                  setCurrentRate(null);
                  setIsEditing(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">收費項目</label>
                <input
                  type="text"
                  value={currentRate.service_type}
                  onChange={(e) => setCurrentRate({...currentRate, service_type: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">收費類型 *</label>
                <select
                  value={currentRate.charge_type || 'fixed'}
                  onChange={(e) => {
                    const chargeType = e.target.value as 'fixed' | 'percentage';
                    setCurrentRate({
                      ...currentRate,
                      charge_type: chargeType,
                      unit: chargeType === 'percentage' ? '%' : (currentRate.unit || '小時')
                    });
                  }}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                >
                  <option value="fixed">固定金額</option>
                  <option value="percentage">服務費%（填 50 收一半、110 含服務費）</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {currentRate.charge_type === 'percentage' ? '百分比 (%)' : '單價'}
                </label>
                <div className="mt-1 relative">
                  <input
                    type="text"
                    value={currentRate.charge_type === 'percentage' 
                      ? (currentRate.unit_price ? `${currentRate.unit_price}%` : '')
                      : formatNumberInput(currentRate.unit_price)}
                    onChange={(e) => {
                      if (currentRate.charge_type === 'percentage') {
                        // 百分比：移除 % 符號後解析
                        const cleaned = e.target.value.replace('%', '').trim();
                        const parsed = parseNumberInput(cleaned);
                        setCurrentRate({...currentRate, unit_price: parsed});
                      } else {
                        // 固定金額：使用原有邏輯
                        const parsed = parseNumberInput(e.target.value);
                        setCurrentRate({...currentRate, unit_price: parsed});
                      }
                    }}
                    onBlur={(e) => {
                      if (currentRate.charge_type === 'percentage') {
                        const cleaned = e.target.value.replace('%', '').trim();
                        const parsed = parseNumberInput(cleaned);
                        setCurrentRate({...currentRate, unit_price: parsed});
                      } else {
                        const parsed = parseNumberInput(e.target.value);
                        setCurrentRate({...currentRate, unit_price: parsed});
                      }
                    }}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder={currentRate.charge_type === 'percentage' ? '100%（實報實銷）或 110%' : '0'}
                  />
                </div>
                {currentRate.charge_type === 'percentage' && (
                  <p className="mt-1 text-xs text-gray-500">
                    報價採購區不讀本表、無牌價；僅填實際採購金額與收費比例。50% 收一半、100% 實報實銷、110% 含 10% 服務費。
                  </p>
                )}
              </div>
              {currentRate.charge_type === 'fixed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">單位</label>
                  <input
                    type="text"
                    value={currentRate.unit || '小時'}
                    onChange={(e) => setCurrentRate({...currentRate, unit: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    placeholder="小時、計次等"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">年度</label>
                <input
                  type="number"
                  value={currentRate.year}
                  onChange={(e) => setCurrentRate({...currentRate, year: parseInt(e.target.value) || new Date().getFullYear()})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>


              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateRate(false);
                    setCurrentRate(null);
                    setIsEditing(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={saveRate}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                >
                  儲存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRates; 